import { streamingRemoteFeedback, streamingResponseText, streamingFieldText, streamingFeedbackText } from "../i18n/streamingFeedbackPresentation";
import { type StreamingMessage, useStreamingLanguage, streamMessage, streamException, streamLocal, streamFeedback } from "../i18n/streamingSettingsPresentation";
/**
 * useUserPolicy -- React hook for WSD per-user enforcement policy.
 *
 * Fetches and updates user-level policy settings (e.g., bypass kill stream).
 *
 * Endpoints:
 * - GET  /api/streaming/users/:serverType/:userId/policy
 * - PUT  /api/streaming/users/:serverType/:userId/policy (CSRF protected)
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { getCsrfToken } from "../lib/csrf";

// ============================================================================
// Types
// ============================================================================

export interface UserPolicyData {
  bypassKillStream: boolean;
}

// ============================================================================
// Cache
// ============================================================================

interface CachedPolicy {
  data: UserPolicyData;
  fetchedAt: number;
}

const policyCache = new Map<string, CachedPolicy>();
const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

function buildPolicyCacheKey(serverType: string, userId: string): string {
  return `policy:${serverType}:${userId}`;
}

// ============================================================================
// useUserPolicy
// ============================================================================

export function useUserPolicy(
  serverType: string,
  userId: string,
  enabled: boolean = true
): {
  policy: UserPolicyData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [policy, setPolicy] = useState<UserPolicyData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);
  const abortRef = useRef<AbortController | null>(null);
  const fetchedKeyRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const fetchPolicy = useCallback(
    async (force?: boolean) => {
      if (!serverType || !userId || !enabled) return;

      const key = buildPolicyCacheKey(serverType, userId);

      // Check cache (unless forced)
      if (!force) {
        const cached = policyCache.get(key);
        if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
          setPolicy(cached.data);
          setError(null);
          fetchedKeyRef.current = key;
          return;
        }
      }

      // Skip if already fetching this key
      if (isFetchingRef.current && fetchedKeyRef.current === key) return;

      // Abort any in-flight request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        const url = `/api/streaming/users/${encodeURIComponent(serverType)}/${encodeURIComponent(userId)}/policy`;
        const res = await fetch(url, { signal: controller.signal });
        const json = await res.json();

        if (!res.ok || !json.ok || !json.data) {
          throw streamException((streamingRemoteFeedback(json) ?? json.message ?? streamLocal("failedToLoadUserPolicy")));
        }

        const fetched = json.data as UserPolicyData;
        setPolicy(fetched);
        setError(null);

        // Cache
        policyCache.set(key, { data: fetched, fetchedAt: Date.now() });
        fetchedKeyRef.current = key;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        const message = streamFeedback(err, "failedToLoadUserPolicy");
        setError(message);
      } finally {
        isFetchingRef.current = false;
        setIsLoading(false);
      }
    },
    [serverType, userId, enabled]
  );

  // Fetch on mount or when key changes
  useEffect(() => {
    if (!enabled) {
      setPolicy(null);
      setError(null);
      fetchedKeyRef.current = null;
      return;
    }

    const key = buildPolicyCacheKey(serverType, userId);
    if (fetchedKeyRef.current !== key) {
      void fetchPolicy();
    }

    return () => {
      abortRef.current?.abort();
    };
  }, [serverType, userId, enabled, fetchPolicy]);

  const refetch = useCallback(() => {
    void fetchPolicy(true);
  }, [fetchPolicy]);

  return { policy, isLoading, error, refetch };
}

// ============================================================================
// useUpdateUserPolicy
// ============================================================================

export function useUpdateUserPolicy(): {
  updating: boolean;
  updatePolicy: (
    serverType: string,
    userId: string,
    update: Partial<UserPolicyData>
  ) => Promise<UserPolicyData | null>;
} {
  const [updating, setUpdating] = useState(false);

  const updatePolicy = useCallback(
    async (
      serverType: string,
      userId: string,
      update: Partial<UserPolicyData>
    ): Promise<UserPolicyData | null> => {
      setUpdating(true);
      try {
        const csrfToken = getCsrfToken();
        const url = `/api/streaming/users/${encodeURIComponent(serverType)}/${encodeURIComponent(userId)}/policy`;

        const res = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
          body: JSON.stringify(update),
        });

        const json = await res.json();

        if (!res.ok || !json.ok || !json.data) {
          throw streamException((streamingRemoteFeedback(json) ?? json.message ?? streamLocal("failedToUpdateUserPolicy")));
        }

        const result = json.data as UserPolicyData;

        // Update cache
        const key = buildPolicyCacheKey(serverType, userId);
        policyCache.set(key, { data: result, fetchedAt: Date.now() });

        return result;
      } catch (err) {
        const message = streamFeedback(err, "failedToUpdateUserPolicy");
        throw streamException(message);
      } finally {
        setUpdating(false);
      }
    },
    []
  );

  return { updating, updatePolicy };
}
